import React, { useState, useMemo } from 'react';
import { Container, Table, Button, Form, Row, Col, Modal, Tabs, Tab, Alert } from 'react-bootstrap';
import {
  Shield,
  Search,
  Plus,
  Trash2,
  Edit,
  Eye,
  Lock,
  Unlock,
  RotateCcw,
  CheckCircle,
  Calendar,
  Folder,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import { jubileeStorageService } from '../services/jubileeStorageService';
import { useJubileeAuth } from '../auth/JubileeAuthContext';
import JubileeUploadModal from './JubileeUploadModal';
import JubileeVideoPlayerModal from '../viewers/JubileeVideoPlayerModal';
import JubileeImageLightbox from '../viewers/JubileeImageLightbox';
import JubileeDocumentViewerModal from '../viewers/JubileeDocumentViewerModal';

export default function JubileeAdminManagement({ onPlayAudioTrack }) {
  const { currentUser, canManage, canDelete } = useJubileeAuth();

  const [mediaList, setMediaList] = useState(() => jubileeStorageService.getAllMedia());
  const [eventsList, setEventsList] = useState(() => jubileeStorageService.getEvents());
  const [categoriesList, setCategoriesList] = useState(() => jubileeStorageService.getCategories());

  // Search, Filters & Sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [sortBy, setSortBy] = useState('newest'); // 'newest' | 'year' | 'title'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // New Category / Event state
  const [newEvent, setNewEvent] = useState({ title: '', year: '2026', category: 'Jubilee Celebrations', description: '', location: '' });
  const [newCat, setNewCat] = useState({ name: '', description: '', icon: 'Folder' });
  const [feedbackMsg, setFeedbackMsg] = useState('');

  // Preview modals
  const [previewVideo, setPreviewVideo] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewImageIdx, setPreviewImageIdx] = useState(0);
  const [showImagePreview, setShowImagePreview] = useState(false);

  const refreshData = () => {
    setMediaList(jubileeStorageService.getAllMedia());
    setEventsList(jubileeStorageService.getEvents());
    setCategoriesList(jubileeStorageService.getCategories());
  };

  // Filtered & Sorted Media
  const processedMedia = useMemo(() => {
    let result = [...mediaList];

    if (filterType !== 'all') {
      result = result.filter((m) => m.mediaType === filterType);
    }
    if (filterVisibility !== 'all') {
      result = result.filter((m) => m.visibility === filterVisibility);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          (m.speaker && m.speaker.toLowerCase().includes(q)) ||
          (m.author && m.author.toLowerCase().includes(q)) ||
          (m.year && String(m.year).includes(q)) ||
          (m.categoryName && m.categoryName.toLowerCase().includes(q))
      );
    }

    if (sortBy === 'newest') {
      result.sort((a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0));
    } else if (sortBy === 'year') {
      result.sort((a, b) => parseInt(b.year || 0, 10) - parseInt(a.year || 0, 10));
    } else if (sortBy === 'title') {
      result.sort((a, b) => a.title.localeCompare(b.title));
    }

    return result;
  }, [mediaList, filterType, filterVisibility, searchQuery, sortBy]);

  // Pagination
  const totalPages = Math.ceil(processedMedia.length / itemsPerPage) || 1;
  const paginatedMedia = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return processedMedia.slice(start, start + itemsPerPage);
  }, [processedMedia, currentPage]);

  const handleToggleVisibility = (id) => {
    jubileeStorageService.toggleMediaVisibility(id);
    refreshData();
  };

  const handleEditClick = (item) => {
    setEditingItem({ ...item });
    setShowEditModal(true);
  };

  const handleSaveEdit = (e) => {
    e.preventDefault();
    if (!editingItem) return;
    jubileeStorageService.updateMediaItem(editingItem.id, editingItem);
    setShowEditModal(false);
    refreshData();
    setFeedbackMsg('Item metadata updated successfully.');
    setTimeout(() => setFeedbackMsg(''), 3000);
  };

  const handleDeleteConfirm = () => {
    if (deletingId) {
      jubileeStorageService.deleteMediaItem(deletingId);
      setDeletingId(null);
      setShowDeleteConfirm(false);
      refreshData();
      setFeedbackMsg('Item successfully removed from the repository.');
      setTimeout(() => setFeedbackMsg(''), 3000);
    }
  };

  const handleCreateEvent = (e) => {
    e.preventDefault();
    if (!newEvent.title.trim()) return;
    jubileeStorageService.addEvent({
      ...newEvent,
      banner: 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80',
    });
    setNewEvent({ title: '', year: '2026', category: 'Jubilee Celebrations', description: '', location: '' });
    refreshData();
    setFeedbackMsg('New Jubilee event added to directory.');
    setTimeout(() => setFeedbackMsg(''), 3000);
  };

  const handleCreateCategory = (e) => {
    e.preventDefault();
    if (!newCat.name.trim()) return;
    jubileeStorageService.addCategory(newCat);
    setNewCat({ name: '', description: '', icon: 'Folder' });
    refreshData();
    setFeedbackMsg('New archive category created.');
    setTimeout(() => setFeedbackMsg(''), 3000);
  };

  const handlePreview = (item) => {
    if (item.mediaType === 'video') setPreviewVideo(item);
    else if (item.mediaType === 'audio') onPlayAudioTrack && onPlayAudioTrack(item);
    else if (item.mediaType === 'document') setPreviewDoc(item);
    else if (item.mediaType === 'image') {
      const idx = mediaList.filter((m) => m.mediaType === 'image').findIndex((img) => img.id === item.id);
      setPreviewImageIdx(idx >= 0 ? idx : 0);
      setShowImagePreview(true);
    }
  };

  return (
    <div className="py-4">
      <Container>
        {/* Admin Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4 pb-3 border-bottom">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div className="p-2 rounded-3 text-white d-inline-flex" style={{ background: '#0f172a' }}>
                <Shield size={22} />
              </div>
              <h2 className="fw-bold m-0 text-slate-900" style={{ letterSpacing: '-0.5px' }}>
                Archival Media Management
              </h2>
            </div>
            <p className="text-muted small m-0">
              Authenticated Session: <strong>{currentUser?.name}</strong> ({currentUser?.role?.toUpperCase()})
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button
              variant="success"
              className="d-flex align-items-center gap-2 rounded-pill px-3 shadow-sm fw-semibold"
              onClick={() => setShowUploadModal(true)}
            >
              <Plus size={18} /> Ingest New Record
            </Button>
          </div>
        </div>

        {feedbackMsg && (
          <Alert variant="success" className="py-2 small d-flex align-items-center gap-2">
            <CheckCircle size={16} /> {feedbackMsg}
          </Alert>
        )}

        <Tabs defaultActiveKey="media" className="mb-4">
          {/* TAB 1: MEDIA MANAGEMENT TABLE */}
          <Tab eventKey="media" title={`Archival Items (${processedMedia.length})`}>
            {/* Filter & Search Bar */}
            <div className="bg-white p-3 rounded-4 shadow-sm border mb-4">
              <Row className="g-2 align-items-center">
                <Col xs={12} md={4}>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light border-end-0">
                      <Search size={15} className="text-muted" />
                    </span>
                    <Form.Control
                      type="text"
                      placeholder="Search archive repository..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="border-start-0"
                    />
                  </div>
                </Col>

                <Col xs={6} md={2}>
                  <Form.Select
                    size="sm"
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="all">All Types</option>
                    <option value="video">Videos</option>
                    <option value="audio">Audio</option>
                    <option value="image">Photos</option>
                    <option value="document">Documents</option>
                  </Form.Select>
                </Col>

                <Col xs={6} md={2}>
                  <Form.Select
                    size="sm"
                    value={filterVisibility}
                    onChange={(e) => {
                      setFilterVisibility(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="all">All Visibility</option>
                    <option value="public">Public</option>
                    <option value="restricted">Restricted</option>
                  </Form.Select>
                </Col>

                <Col xs={6} md={2}>
                  <Form.Select
                    size="sm"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="newest">Sort: Newest</option>
                    <option value="year">Sort: Year</option>
                    <option value="title">Sort: Title</option>
                  </Form.Select>
                </Col>

                <Col xs={6} md={2} className="text-end">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setSearchQuery('');
                      setFilterType('all');
                      setFilterVisibility('all');
                      setSortBy('newest');
                    }}
                    className="w-100 d-flex align-items-center justify-content-center gap-1"
                  >
                    <RotateCcw size={13} /> Reset
                  </Button>
                </Col>
              </Row>
            </div>

            {/* Table */}
            <div className="table-responsive jubilee-admin-table bg-white border rounded-4 shadow-sm mb-3">
              <Table hover className="m-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>Thumbnail</th>
                    <th>Title & Provenance</th>
                    <th>Type</th>
                    <th>Event</th>
                    <th>Year</th>
                    <th>Category</th>
                    <th>Visibility</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMedia.map((item) => {
                    const isVid = item.mediaType === 'video';
                    const isAud = item.mediaType === 'audio';
                    const isImg = item.mediaType === 'image';

                    return (
                      <tr key={item.id}>
                        <td>
                          <img
                            src={item.thumbnailUrl}
                            alt=""
                            className="rounded shadow-xs object-fit-cover"
                            style={{ width: '56px', height: '40px', background: '#0f172a' }}
                          />
                        </td>
                        <td>
                          <div className="fw-semibold text-dark text-truncate" style={{ maxWidth: '280px' }}>
                            {item.title}
                          </div>
                          <div className="text-muted text-xs text-truncate" style={{ maxWidth: '280px' }}>
                            {item.speaker || item.author ? `By: ${item.speaker || item.author}` : item.fileName}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge text-capitalize ${
                              isVid ? 'bg-danger' : isAud ? 'bg-primary' : isImg ? 'bg-warning text-dark' : 'bg-success'
                            }`}
                          >
                            {item.mediaType}
                          </span>
                        </td>
                        <td>
                          <span className="small text-muted text-truncate d-block" style={{ maxWidth: '160px' }}>
                            {item.eventName || 'General'}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border">{item.year}</span>
                        </td>
                        <td>
                          <span className="small text-secondary text-truncate d-block" style={{ maxWidth: '140px' }}>
                            {item.categoryName}
                          </span>
                        </td>
                        <td>
                          <button
                            className={`btn btn-sm ${item.visibility === 'public' ? 'btn-outline-success' : 'btn-outline-warning'} py-0 px-2 text-xs rounded-pill d-inline-flex align-items-center gap-1`}
                            onClick={() => handleToggleVisibility(item.id)}
                            title="Click to toggle visibility"
                          >
                            {item.visibility === 'public' ? <Unlock size={12} /> : <Lock size={12} />}
                            {item.visibility}
                          </button>
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-1">
                            <button
                              className="btn btn-sm btn-outline-secondary p-1 rounded"
                              onClick={() => handlePreview(item)}
                              title="Preview"
                            >
                              <Eye size={15} />
                            </button>
                            {canManage && (
                              <button
                                className="btn btn-sm btn-outline-primary p-1 rounded"
                                onClick={() => handleEditClick(item)}
                                title="Edit Metadata"
                              >
                                <Edit size={15} />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                className="btn btn-sm btn-outline-danger p-1 rounded"
                                onClick={() => {
                                  setDeletingId(item.id);
                                  setShowDeleteConfirm(true);
                                }}
                                title="Delete/Archive"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 bg-white rounded-3 border">
                <span className="small text-muted">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, processedMedia.length)} of {processedMedia.length} records
                </span>
                <div className="d-flex gap-1">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <ChevronLeft size={16} /> Previous
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </Tab>

          {/* TAB 2: MANAGE EVENTS */}
          <Tab eventKey="events" title={`Events Directory (${eventsList.length})`}>
            <Row className="g-4">
              <Col xs={12} lg={4}>
                <div className="bg-white p-4 rounded-4 border shadow-sm">
                  <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                    <Calendar size={18} className="text-success" />
                    Create New Jubilee Event
                  </h6>
                  <Form onSubmit={handleCreateEvent}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small fw-semibold">Event Title</Form.Label>
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="e.g., Jubilee Symposium 2026"
                        value={newEvent.title}
                        onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="small fw-semibold">Year</Form.Label>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={newEvent.year}
                        onChange={(e) => setNewEvent({ ...newEvent, year: e.target.value })}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="small fw-semibold">Location / Venue</Form.Label>
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="e.g., Loyola Hall, Shembaganur"
                        value={newEvent.location}
                        onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-semibold">Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        value={newEvent.description}
                        onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                      />
                    </Form.Group>
                    <Button type="submit" variant="success" size="sm" className="w-100 fw-semibold">
                      Save Event
                    </Button>
                  </Form>
                </div>
              </Col>

              <Col xs={12} lg={8}>
                <div className="bg-white p-4 rounded-4 border shadow-sm">
                  <h6 className="fw-bold mb-3">Existing Events</h6>
                  <div className="d-flex flex-column gap-2">
                    {eventsList.map((ev) => (
                      <div key={ev.id} className="p-3 bg-light rounded-3 border d-flex justify-content-between align-items-center">
                        <div>
                          <span className="badge bg-success bg-opacity-10 text-success me-2">{ev.year}</span>
                          <strong>{ev.title}</strong>
                          <div className="text-muted small">{ev.location} • {ev.category}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          </Tab>

          {/* TAB 3: MANAGE CATEGORIES */}
          <Tab eventKey="categories" title={`Collections / Categories (${categoriesList.length})`}>
            <Row className="g-4">
              <Col xs={12} lg={4}>
                <div className="bg-white p-4 rounded-4 border shadow-sm">
                  <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                    <Folder size={18} className="text-primary" />
                    Create New Category
                  </h6>
                  <Form onSubmit={handleCreateCategory}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small fw-semibold">Category Name</Form.Label>
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="e.g., Missionary Palm Leaves"
                        value={newCat.name}
                        onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-semibold">Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        size="sm"
                        placeholder="Purpose and scope of this collection..."
                        value={newCat.description}
                        onChange={(e) => setNewCat({ ...newCat, description: e.target.value })}
                      />
                    </Form.Group>
                    <Button type="submit" variant="primary" size="sm" className="w-100 fw-semibold">
                      Create Category
                    </Button>
                  </Form>
                </div>
              </Col>

              <Col xs={12} lg={8}>
                <div className="bg-white p-4 rounded-4 border shadow-sm">
                  <h6 className="fw-bold mb-3">Configured Categories</h6>
                  <div className="d-flex flex-column gap-2">
                    {categoriesList.map((c) => (
                      <div key={c.id} className="p-3 bg-light rounded-3 border d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{c.name}</strong>
                          <div className="text-muted small">{c.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </Container>

      {/* Edit Metadata Modal */}
      {editingItem && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-light">
            <Modal.Title className="fs-6 fw-bold">Edit Archival Record Metadata</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSaveEdit}>
            <Modal.Body className="p-4">
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Record Title</Form.Label>
                <Form.Control
                  type="text"
                  value={editingItem.title}
                  onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editingItem.description}
                  onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                />
              </Form.Group>

              <Row className="g-3 mb-3">
                <Col xs={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Speaker / Author</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingItem.speaker || editingItem.author || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, speaker: e.target.value, author: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Year</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingItem.year}
                      onChange={(e) => setEditingItem({ ...editingItem, year: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col xs={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Category</Form.Label>
                    <Form.Select
                      value={editingItem.categoryId}
                      onChange={(e) => {
                        const sel = categoriesList.find((c) => c.id === e.target.value);
                        setEditingItem({ ...editingItem, categoryId: e.target.value, categoryName: sel?.name || '' });
                      }}
                    >
                      {categoriesList.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Visibility</Form.Label>
                    <Form.Select
                      value={editingItem.visibility}
                      onChange={(e) => setEditingItem({ ...editingItem, visibility: e.target.value })}
                    >
                      <option value="public">Public</option>
                      <option value="restricted">Restricted</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" size="sm" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="success" size="sm">
                Save Changes
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered size="sm">
        <Modal.Body className="p-4 text-center">
          <div className="p-3 bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex mb-3">
            <AlertTriangle size={32} />
          </div>
          <h5 className="fw-bold">Confirm Archival Deletion</h5>
          <p className="text-muted small mb-4">
            Are you sure you want to delete this record from the Platinum Jubilee repository? This action cannot be undone.
          </p>
          <div className="d-flex justify-content-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={handleDeleteConfirm}>
              Delete Record
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Upload Ingestion Modal */}
      <JubileeUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onUploadSuccess={() => {
          refreshData();
          setFeedbackMsg('New archival file ingested successfully.');
          setTimeout(() => setFeedbackMsg(''), 3000);
        }}
      />

      {/* Media Previews */}
      <JubileeVideoPlayerModal
        video={previewVideo}
        show={!!previewVideo}
        onHide={() => setPreviewVideo(null)}
        allVideos={mediaList.filter((m) => m.mediaType === 'video')}
      />

      <JubileeDocumentViewerModal
        document={previewDoc}
        show={!!previewDoc}
        onHide={() => setPreviewDoc(null)}
      />

      <JubileeImageLightbox
        images={mediaList.filter((m) => m.mediaType === 'image')}
        selectedIndex={previewImageIdx}
        show={showImagePreview}
        onHide={() => setShowImagePreview(false)}
        onSelectIndex={(i) => setPreviewImageIdx(i)}
      />
    </div>
  );
}
